import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Responsive, WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import { FiRefreshCw, FiDownload, FiMaximize, FiMinimize, FiX, FiLock, FiUnlock, FiFilter, FiCalendar, FiSearch, FiChevronDown, FiChevronRight, FiChevronUp, FiEdit, FiSettings, FiSun, FiMoon, FiShare2, FiFileText, FiInfo, FiAlertTriangle } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { useAuth } from '../App'
import ChartWidget, { TankView } from '../components/charts/ChartWidget'
import CariDetayPanel from '../components/CariDetayPanel'
import useIsMobile from '../utils/useIsMobile'

const ResponsiveGridLayout = WidthProvider(Responsive)

export default function DashboardView() {
  const { id } = useParams()
  const { user, savePreference } = useAuth()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [dashboard, setDashboard] = useState(null)
  // activePage ve showMode dashboard bazli localStorage'da tutulur — sayfa yenilenince kalinan yerden devam
  // user_id ilk render'da null olabilir (auth /me sonradan yuklenir), o yuzden anahtarlar user-agnostic
  const _dashKey = (k) => `${k}_dash_${id}`
  const [searchParams, setSearchParams] = useSearchParams()
  // Sayfa adı -> kalıcı URL slug'ı (esnafsoruyor linkleri: /dashboard/17?sekme=karlilik)
  const _slug = (s) => (s || '').toString().toLowerCase()
    .replace(/ı/g, 'i').replace(/İ/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const [activePage, setActivePageRaw] = useState(() => {
    const v = parseInt(localStorage.getItem(_dashKey('activePage')) || '0', 10)
    return isNaN(v) ? 0 : v
  })
  const setActivePage = (v) => {
    const n = typeof v === 'function' ? v(activePage) : v
    localStorage.setItem(_dashKey('activePage'), String(n))
    setActivePageRaw(n)
    // Aktif sekmeyi URL'ye yaz (kalıcı, paylaşılabilir link)
    const pg = dashboard?.pages?.[n]
    if (pg) setSearchParams({ sekme: _slug(pg.name) }, { replace: true })
  }
  // Sayfa degisince cari detay panelini kapat
  useEffect(() => { window.dispatchEvent(new CustomEvent('cari-detay-close')) }, [activePage])
  const [showMode, setShowModeRaw] = useState(() => {
    const v = localStorage.getItem(_dashKey('showMode'))
    return v === 'miktar' ? 'miktar' : 'tutar'
  })
  const setShowMode = (v) => {
    localStorage.setItem(_dashKey('showMode'), v)
    setShowModeRaw(v)
  }
  // Gorunum (Kisa/Tum) - sayilarin nasil formatlanacagini belirler
  // Hem genel 'numberView' hem user-bazli'yi okur (auth gec gelirse fallback)
  const [numberView, setNumberViewState] = useState(() => {
    const v = localStorage.getItem(`numberView_user_${user?.id}`)
          || localStorage.getItem('numberView')
          || 'short'
    return v === 'all' ? 'all' : 'short'
  })
  // user gec yuklenirse (App'in /auth/me cevabi sonra geliyor) tercihi yeniden oku
  useEffect(() => {
    if (!user?.id) return
    const v = localStorage.getItem(`numberView_user_${user.id}`)
          || localStorage.getItem('numberView')
    if (v === 'all' || v === 'short') setNumberViewState(v)
  }, [user?.id])
  const setNumberView = (v) => {
    localStorage.setItem(`numberView_user_${user?.id || 'anon'}`, v)
    localStorage.setItem('numberView', v)
    setNumberViewState(v)
    savePreference && savePreference('numberView', v)
  }
  // Tema (light/dark)
  const [theme, setThemeState] = useState(() => localStorage.getItem(`theme_user_${user?.id || 'anon'}`) || 'light')
  const setTheme = (v) => {
    localStorage.setItem(`theme_user_${user?.id || 'anon'}`, v)
    localStorage.setItem('theme', v)
    if (v === 'dark') document.documentElement.classList.add('dark')
    else document.documentElement.classList.remove('dark')
    setThemeState(v)
    savePreference && savePreference('theme', v)
  }
  // Sayfa acilirken tema class'ini uygula
  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark')
    else document.documentElement.classList.remove('dark')
  }, [])
  // Renk paleti
  const [colorPalette, setColorPaletteState] = useState(() => localStorage.getItem(`colorPalette_user_${user?.id || 'anon'}`) || 'default')
  const setColorPalette = (v) => {
    localStorage.setItem(`colorPalette_user_${user?.id || 'anon'}`, v)
    localStorage.setItem('colorPalette', v)
    setColorPaletteState(v)
    savePreference && savePreference('colorPalette', v)
  }
  // Tablo sutun boyutu (padding) ve deger boyutu (font)
  const [tableColSize, setTableColSizeState] = useState(() => localStorage.getItem(`tableColSize_user_${user?.id || 'anon'}`) || 'md')
  const setTableColSize = (v) => {
    localStorage.setItem(`tableColSize_user_${user?.id || 'anon'}`, v)
    localStorage.setItem('tableColSize', v)
    setTableColSizeState(v)
    savePreference && savePreference('tableColSize', v)
  }
  const [tableFontSize, setTableFontSizeState] = useState(() => localStorage.getItem(`tableFontSize_user_${user?.id || 'anon'}`) || 'md')
  const setTableFontSize = (v) => {
    localStorage.setItem(`tableFontSize_user_${user?.id || 'anon'}`, v)
    localStorage.setItem('tableFontSize', v)
    setTableFontSizeState(v)
    savePreference && savePreference('tableFontSize', v)
  }
  // Px bazlı tablo yazı boyutu (slider) — tanımlıysa küçük/orta/büyük'ün önüne geçer
  const [tableFontPx, setTableFontPxState] = useState(() => Number(localStorage.getItem('tableFontSizePx')) || 15)
  const setTableFontPx = (v) => {
    localStorage.setItem('tableFontSizePx', String(v))
    setTableFontPxState(v)
    savePreference && savePreference('tableFontSizePx', v)
  }
  // Ilk render'da global key'leri user'a ozel degerlerle senkronize et
  useEffect(() => {
    localStorage.setItem('numberView', numberView)
    localStorage.setItem('colorPalette', colorPalette)
  }, [])
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false)
  const settingsRef = useRef(null)
  // Karlilik sayfasi maliyet hesaplama yontemi
  const [costMethod, setCostMethodState] = useState(() => localStorage.getItem(`costMethod_user_${user?.id || 'anon'}`) || 'agirlikli')
  const setCostMethod = (v) => {
    localStorage.setItem(`costMethod_user_${user?.id || 'anon'}`, v)
    setCostMethodState(v)
  }
  const COST_METHOD_LABELS = {
    en_son: 'En Son', ortalama: 'Ortalama', agirlikli: 'Ortalama Agirlikli',
    en_ucuz: 'En Ucuz', en_pahali: 'En Pahali', fifo: 'FIFO'
  }
  const widgetTitle = (w) => (w.id === 265 || w.id === 275) ? `Kar (${COST_METHOD_LABELS[costMethod] || costMethod})` : w.title
  // Lojistik sayfalari icin doviz filtresi
  const [currencyFilter, setCurrencyFilterState] = useState(() => localStorage.getItem(`currency_user_${user?.id || 'anon'}`) || '')
  const setCurrencyFilter = (v) => {
    localStorage.setItem(`currency_user_${user?.id || 'anon'}`, v)
    setCurrencyFilterState(v)
  }
  const prevCurrency = useRef(currencyFilter)
  useEffect(() => {
    if (prevCurrency.current !== currencyFilter && dashboard?.pages[activePage]) {
      prevCurrency.current = currencyFilter
      loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [currencyFilter])
  // Lojistik: Yurt Ici/Disi filtresi
  const [tasimaTuru, setTasimaTuruState] = useState(() => localStorage.getItem(`tasimaTuru_user_${user?.id || 'anon'}`) || '')
  const setTasimaTuru = (v) => {
    localStorage.setItem(`tasimaTuru_user_${user?.id || 'anon'}`, v)
    setTasimaTuruState(v)
  }
  const prevTasima = useRef(tasimaTuru)
  useEffect(() => {
    if (prevTasima.current !== tasimaTuru && dashboard?.pages[activePage]) {
      prevTasima.current = tasimaTuru
      loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [tasimaTuru])
  // Pocan Petrol: Sube filtresi
  const [pocanSube, setPocanSube] = useState(() => localStorage.getItem(`pocanSube_user_${user?.id || 'anon'}`) || '')
  const prevSube = useRef(pocanSube)
  useEffect(() => {
    localStorage.setItem(`pocanSube_user_${user?.id || 'anon'}`, pocanSube)
    if (prevSube.current !== pocanSube && dashboard?.pages[activePage]) {
      prevSube.current = pocanSube
      // Karsilastirma sayfasi tum<->diger gecislerinde layout'u yeniden insa et (yan yana 2 kopya)
      if (dashboard.pages[activePage].name === 'Karsilastirma') {
        buildLayouts(dashboard.pages[activePage])
      }
      loadPageData(dashboard.pages[activePage], activeFilters, { sube: pocanSube })
    }
  }, [pocanSube])
  // Lojistik: Sefer Durumu, Arac Tipi, Plaka filtreleri
  const [seferDurumu, setSeferDurumu] = useState('')
  const [aracTipi, setAracTipi] = useState('')
  const [aracTuru, setAracTuru] = useState('')  // Çekici=1 / Dorse=2 / Minivan=3 (yurtdışı araç türü)
  // Para modu kullanici bazinda hatirlanir: bir sayfada ₺ secip baska sayfaya/panoya
  // gecince secim korunur (qr22 — "doviz tl cevrimi tum sitede gozuksun").
  const [currencyMode, setCurrencyModeState] = useState(
    () => localStorage.getItem(`currencyMode_user_${user?.id || 'anon'}`)
       || localStorage.getItem('currencyMode') || '')  // '' (Döviz/€) | 'tl' (TL Karşılığı: tutar*kur)
  const setCurrencyMode = (v) => {
    localStorage.setItem(`currencyMode_user_${user?.id || 'anon'}`, v)
    localStorage.setItem('currencyMode', v)
    setCurrencyModeState(v)
  }
  const [plakaFilter, setPlakaFilter] = useState('')   // UYGULANAN filtre (sadece secimle degisir)
  const [plakaList, setPlakaList] = useState([])       // [{plaka, turu, tip, aktif, hareket}]
  const [plakaQ, setPlakaQ] = useState('')             // arama kutusundaki metin
  const [plakaAcik, setPlakaAcik] = useState(false)
  const prevSD = useRef(seferDurumu); const prevAT = useRef(aracTipi); const prevATU = useRef(aracTuru); const prevCM = useRef(currencyMode); const prevPL = useRef(plakaFilter)
  useEffect(() => {
    if (dashboard?.name?.toLowerCase().includes('lojistik')) {
      api.get('/widgets/lojistik-options').then(({ data }) => {
        const det = data.plaka_detay
        setPlakaList(Array.isArray(det) && det.length ? det
          : (data.plakas || []).map(x => ({ plaka: x, turu: '', tip: '', hareket: 0 })))
      }).catch(()=>{})
    }
  }, [dashboard?.name])
  useEffect(() => {
    if (prevSD.current !== seferDurumu && dashboard?.pages[activePage]) {
      prevSD.current = seferDurumu; loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [seferDurumu])
  useEffect(() => {
    if (prevAT.current !== aracTipi && dashboard?.pages[activePage]) {
      prevAT.current = aracTipi; loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [aracTipi])
  useEffect(() => {
    if (prevATU.current !== aracTuru && dashboard?.pages[activePage]) {
      prevATU.current = aracTuru; loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [aracTuru])
  useEffect(() => {
    if (prevCM.current !== currencyMode && dashboard?.pages[activePage]) {
      prevCM.current = currencyMode; loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [currencyMode])
  useEffect(() => {
    if (prevPL.current !== plakaFilter && dashboard?.pages[activePage]) {
      prevPL.current = plakaFilter; loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [plakaFilter])
  useEffect(() => {
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  // costMethod degisince Karlilik sayfasini yenile
  const prevCostMethod = useRef(costMethod)
  useEffect(() => {
    if (prevCostMethod.current !== costMethod && dashboard?.pages[activePage]) {
      prevCostMethod.current = costMethod
      loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [costMethod])
  // showMode degisince pivot widget'lar icin veri yeniden yukle
  const prevShowMode = useRef(showMode)
  useEffect(() => {
    if (prevShowMode.current !== showMode && dashboard?.pages[activePage]) {
      prevShowMode.current = showMode
      loadPageData(dashboard.pages[activePage], activeFilters)
    }
  }, [showMode])
  const [widgetData, setWidgetData] = useState({})
  // Karsilastirma sayfasi + Tum modu icin iki ayri istasyon verisi
  const [widgetDataAlt, setWidgetDataAlt] = useState({})
  const [widgetDataTat, setWidgetDataTat] = useState({})
  const [activeFilters, setActiveFilters] = useState({})
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [petrolUpdating, setPetrolUpdating] = useState(false)  // "Verileri Güncelle" (Firebird->DuckDB ETL)
  const [fullscreenWidget, setFullscreenWidget] = useState(null)
  const [infoWidget, setInfoWidget] = useState(null)  // {title, info}
  const [editMode, setEditMode] = useState(false)
  // Mobil: aractlar panel (Miktar/Tutar, Refresh, Download, Sube, Cost Method, Ayarlar) acilir kapanir
  // Default acik — kullanici alt-kenardaki kucuk ok ile gizleyip acabilir (banner tarzi)
  const [toolsOpen, setToolsOpen] = useState(true)
  const [layouts, setLayouts] = useState({})
  // Dashboard ekrana sığmıyorsa orantılı küçült (CSS zoom — en/boy korunur, daraltma yok)
  const canvasRef = useRef(null)
  const [fitZoom, setFitZoom] = useState(1)

  // Filtre paneli
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [filterOptions, setFilterOptions] = useState({})
  const [filterSearch, setFilterSearch] = useState({})
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Musteri Takip sayfasi ust filtreleri (sefer sayisi / son sefer gun araligi)
  const [mtSeferMin, setMtSeferMin] = useState('')
  const [mtSeferMax, setMtSeferMax] = useState('')
  const [mtGunMin, setMtGunMin] = useState('')
  const [mtGunMax, setMtGunMax] = useState('')

  // Cross-filter state
  const [crossFilterSource, setCrossFilterSource] = useState(null) // { widgetId, column, value }

  useEffect(() => { loadDashboard() }, [id])

  // Veri kaynağı uyarısı: dashboard açılınca o an çekilemeyen veriler (ETL / tank) pop-up ile gösterilir,
  // Tamam'a basınca kapanır. Backend: GET /api/veri-durumu/{id} (services/veri_durumu.py)
  const [veriSorunlari, setVeriSorunlari] = useState([])
  useEffect(() => {
    let iptal = false
    setVeriSorunlari([])
    api.get(`/veri-durumu/${id}`)
      .then(({ data }) => { if (!iptal) setVeriSorunlari(data?.sorunlar || []) })
      .catch(() => {})
    return () => { iptal = true }
  }, [id])
  const _sonBasarili = (iso) => {
    if (!iso) return 'bilinmiyor'
    return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  }

  // Dashboard ekrana sığmıyorsa orantılı küçült (zoom). Sığıyorsa 1 (dokunma). Mobil/düzenleme modunda kapalı.
  useEffect(() => {
    if (isMobile || editMode) { setFitZoom(1); return }  // tank dahil tüm sayfalar monitöre göre küçülür
    const fit = () => {
      const el = canvasRef.current
      if (!el) return
      const realContent = el.scrollHeight / (fitZoom || 1)  // mevcut zoom'u geri al → gerçek içerik yüksekliği
      const avail = el.clientHeight
      // En fazla 0.78'e kadar küçült — daha agresifi yazıları bulanıklaştırıyor (tablo sayfaları zaten kendi içinde kaydırılır)
      const z = realContent > avail + 4 ? Math.max(0.78, avail / realContent) : 1
      if (Math.abs(z - fitZoom) > 0.01) setFitZoom(z)
    }
    const t = setTimeout(fit, 400)  // grid render + grafik çizimi sonrası ölç
    window.addEventListener('resize', fit)
    return () => { clearTimeout(t); window.removeEventListener('resize', fit) }
  }, [widgetData, activePage, editMode, isMobile, fitZoom, dashboard])

  const loadDashboard = async () => {
    try {
      const { data } = await api.get(`/dashboards/${id}`)
      // Pasif sayfalari goruntuleme ekraninda gizle (Designer'da gozukur ama burada hayir)
      data.pages = (data.pages || []).filter(p => p.is_active !== false)
      setDashboard(data)
      if (data.pages.length > 0) {
        // F5 sonrasi localStorage'dan gelen activePage'i clamp et (sayfa silinmis olabilir)
        let initPageIdx = Math.min(Math.max(0, activePage), data.pages.length - 1)
        // URL'de ?sekme= varsa ONA öncelik ver (kalıcı/paylaşılan link -> doğrudan o sekme açılır)
        const _urlSekme = searchParams.get('sekme')
        if (_urlSekme) {
          const _fi = data.pages.findIndex(p => _slug(p.name) === _urlSekme)
          if (_fi >= 0) initPageIdx = _fi
        }
        if (initPageIdx !== activePage) setActivePage(initPageIdx)
        buildLayouts(data.pages[initPageIdx])
        // dashId'yi EXPLICIT gecir: setDashboard async oldugundan ilk yuklemede state henuz bos
        // -> lojistik tasima_turu override'i (dashId 11/14) uygulanmadan FILTRESIZ veri geliyordu
        loadPageData(data.pages[initPageIdx], {}, { dashId: data.id })
        // Diger sayfalari arka planda yukle - filter options icin (hic gecikmesiz)
        preloadAllFilterOptions(data)
      }
    } catch (err) {
      toast.error('Dashboard yuklenemedi')
    } finally {
      setLoading(false)
    }
  }

  // Tum dashboard widget'larini arka planda cek -> filterOptions'a ekle
  const preloadAllFilterOptions = async (dashboardData) => {
    try {
      const allIds = []
      dashboardData.pages.forEach(p => {
        p.widgets.forEach(w => {
          if (w.variable_id || w.config?._pivot) allIds.push(w.id)
        })
      })
      if (allIds.length === 0) return
      const { data: allData } = await api.post('/widgets/batch-data',
        { widget_ids: allIds, filters: '{}', show_mode: 'tutar', cost_method: 'agirlikli' })
      extractFilterOptions(allData)
    } catch (e) { /* sessizce gec */ }
  }

  const isCompareTum = (page) => page?.name === 'Karsilastirma' && pocanSube === 'tum'

  const buildLayouts = (page) => {
    const lg = page.widgets.map(w => ({
      i: String(w.id), x: w.grid.x, y: w.grid.y, w: w.grid.w, h: w.grid.h, minW: 2, minH: 2,
    }))
    setLayouts({ lg, md: lg, sm: lg })
  }

  const loadPageData = async (page, filters, opts = {}) => {
    setRefreshing(true)
    const live = opts.live === true
    // Sube: cagiran explicit gecerse onu kullan (stale closure'i onler), yoksa state
    const effectiveSube = opts.sube !== undefined ? opts.sube : pocanSube
    const widgetIds = page.widgets.filter(w => w.variable_id || (w.config?.datasource_id && w.config?.table_name) || w.config?._tank_data || w.config?._pivot).map(w => w.id)
    const filtersStr = Object.keys(filters).length > 0 ? JSON.stringify(filters) : undefined
    // Dashboard'a göre tasima_turu otomatik override (Pocan Lojistik Yurt İçi=11, Yurt Dışı=14)
    // opts.dashId: ilk yuklemede state henuz set olmadigindan explicit gecilir
    const dashId = opts.dashId !== undefined ? opts.dashId : dashboard?.id
    const effectiveTasima = dashId === 11 ? 'YI' : dashId === 14 ? 'YD' : tasimaTuru

    // Karsilastirma + Tum modunda 3 sube verisini birlestir + ISTASYON sutunu ekle (overlay)
    if (page?.name === 'Karsilastirma' && effectiveSube === 'tum') {
      try {
        const baseParams = { widget_ids: widgetIds, filters: filtersStr, show_mode: showMode, cost_method: costMethod, currency: currencyFilter, tasima_turu: effectiveTasima, plaka: plakaFilter, arac_tipi: aracTipi, arac_turu: aracTuru, currency_mode: currencyMode, sefer_durumu: seferDurumu, live }
        const [resA, resT, resL] = await Promise.all([
          api.post('/widgets/batch-data', { ...baseParams, sube: 'altinekin' }),
          api.post('/widgets/batch-data', { ...baseParams, sube: 'tatlicak' }),
          api.post('/widgets/batch-data', { ...baseParams, sube: 'ladik' }),
        ])
        const merged = {}
        const allKeys = new Set([
          ...Object.keys(resA.data || {}),
          ...Object.keys(resT.data || {}),
          ...Object.keys(resL.data || {}),
        ])
        allKeys.forEach(k => {
          // Pivot widget: ISTASYON kolonuyla merge yerine her sube ayri tablo (stations)
          const kw = page.widgets.find(w => String(w.id) === String(k))
          if (kw?.config?._pivot) {
            const mkSt = (res, name) => ({ name, data: res.data?.[k]?.data || [], columns: res.data?.[k]?.columns || [], row_count: res.data?.[k]?.row_count || 0 })
            const stations = [mkSt(resA, 'Altınekin'), mkSt(resT, 'Tatlıcak'), mkSt(resL, 'Ladık')]
            merged[k] = { data: [], columns: [], row_count: stations.reduce((s, x) => s + x.row_count, 0), stations }
            return
          }
          const a = (resA.data?.[k]?.data || []).map(r => ({ ...r, ISTASYON: 'Altinekin' }))
          const t = (resT.data?.[k]?.data || []).map(r => ({ ...r, ISTASYON: 'Tatlicak' }))
          const l = (resL.data?.[k]?.data || []).map(r => ({ ...r, ISTASYON: 'Ladik' }))
          const cols = resA.data?.[k]?.columns || resT.data?.[k]?.columns || resL.data?.[k]?.columns || []
          const newCols = cols.some(c => c === 'ISTASYON' || c?.name === 'ISTASYON') ? cols : [...cols, 'ISTASYON']
          merged[k] = { data: [...a, ...t, ...l], columns: newCols, row_count: a.length + t.length + l.length }
        })
        setWidgetData(merged)
        if (!filtersStr) extractFilterOptions(merged)
      } catch (err) {
        // Yenileme/yukleme hatasi (uzak Firebird timeout vb.): ONCEKI veriyi KORU,
        // setWidgetData({}) yapma -> "veri yok" flash'i olusmasin.
      }
      setRefreshing(false)
      return
    }

    try {
      const { data } = await api.post('/widgets/batch-data', { widget_ids: widgetIds, filters: filtersStr, show_mode: showMode, cost_method: costMethod, currency: currencyFilter, tasima_turu: effectiveTasima, plaka: plakaFilter, arac_tipi: aracTipi, arac_turu: aracTuru, currency_mode: currencyMode, sefer_durumu: seferDurumu, sube: effectiveSube, live })
      setWidgetData(data)
      // Filtre seceneklerini sadece ilk yuklemede olustur (filtre yoksa)
      if (!filtersStr) extractFilterOptions(data)
    } catch (err) {
      // batch-data patladi -> widget bazli dene. BASARISIZ olan widget'in ESKI verisini KORU
      // (bos {} ile ezme -> "veri yok" olmasin). Sadece basariyla gelenleri guncelle.
      const fresh = {}
      await Promise.all(widgetIds.map(async (wid) => {
        try {
          const { data } = await api.get(`/widgets/${wid}/data`, { params: { filters: filtersStr } })
          fresh[wid] = data
        } catch (e) { /* bu widget alinamadi -> onceki veriyi koru */ }
      }))
      setWidgetData(prev => ({ ...prev, ...fresh }))
      if (!filtersStr) extractFilterOptions(fresh)
    }
    setRefreshing(false)
  }

  // Widget verilerinden filtre seceneklerini cikar
  const extractFilterOptions = (allData) => {
    const options = {}
    // Buyuk listeli kolonlar (urun adi vs.) icin limit yok
    const LARGE_LIST_COLS = ['STOKADI', 'STOKADI1', 'UNVAN', 'TICARI_UNVANI', 'MUSTERI', 'PLAKA', 'SOFOR']
    Object.values(allData).forEach(wd => {
      if (!wd?.data) return
      wd.data.forEach(row => {
        Object.entries(row).forEach(([col, val]) => {
          if (val == null || val === '') return
          if (typeof val === 'number') return // sayi kolonlari filtre olmaz
          const sval = String(val).trim()   // sondaki bosluklar (CHAR kolonlar) filtreyi ciftlestiriyordu
          if (sval === '' || sval.length > 80) return
          if (!options[col]) options[col] = new Set()
          const isLarge = LARGE_LIST_COLS.includes(col.toUpperCase())
          const cap = isLarge ? 2000 : 100
          if (options[col].size < cap) options[col].add(sval)
        })
      })
    })
    // Set -> Array, gereksiz degerleri cikar
    const hideValues = []
    const result = {}
    Object.entries(options).forEach(([col, vals]) => {
      const filtered = [...vals].filter(v => !hideValues.includes(v.toUpperCase())).sort()
      const isLarge = LARGE_LIST_COLS.includes(col.toUpperCase())
      // Buyuk listeler: 2+ deger varsa al; kucuk listeler: 2-50 arasi
      if (filtered.length >= 2 && (isLarge || filtered.length <= 50)) {
        result[col] = filtered
      }
    })
    // Onceki sayfalardaki filter degerleriyle birlestir (sayfa gezdikce zenginlesir)
    setFilterOptions(prev => {
      const merged = { ...prev }
      Object.entries(result).forEach(([k, v]) => {
        const old = merged[k] || []
        merged[k] = [...new Set([...old, ...v])].sort()
      })
      return merged
    })
  }

  const handleRefresh = async () => {
    const page = dashboard?.pages[activePage]
    if (!page) return
    const pageName = (page.name || '').toLowerCase()
    // Karlilik sayfasinda global cache'i temizle (Quark stored proc yeniden cagrilsin)
    if (pageName.includes('karli')) {
      try { await api.post('/widgets/invalidate-karlilik-cache') } catch {}
    }
    // Pocan Petrol Karlilik DISI sayfalarda 'Tum' (3 sube) icin canli sorgu = 3 DB,
    // cok yavas olabiliyor. Snapshot'tan oku (10dk'da bir guncelleniyor zaten).
    const isPocanPetrolNonKarlilik = dashboard?.id === 8 && !pageName.includes('karli')
    const useLive = !isPocanPetrolNonKarlilik
    loadPageData(page, activeFilters, { live: useLive })
  }

  // Pocan Petrol "Verileri Güncelle": Firebird'den DuckDB'yi yenile (ETL) + snapshot'lar.
  // Quark'ta fatura/veri düzeltince kullanıcı buna basar; arka planda birkaç dk sürer.
  const updatePetrolData = async () => {
    if (petrolUpdating) return
    setPetrolUpdating(true)
    try {
      const isMarket = dashboard?.id === 10
      const pid = dashboard?.pages?.[activePage]?.id
      await api.post(`/widgets/refresh-petrol-firebird?${isMarket ? 'market=true' : (pid ? `page_id=${pid}` : '')}`)
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get('/widgets/refresh-petrol-firebird/status')
          if (!data.running) {
            clearInterval(poll)
            setPetrolUpdating(false)
            const page = dashboard?.pages[activePage]
            if (page) loadPageData(page, activeFilters, { live: false })  // güncel snapshot'ı çek
            if (data.error) alert('Güncelleme tamamlandı ama hata oldu: ' + data.error)
          }
        } catch {}
      }, 5000)
      // Güvenlik: 12 dk sonra polling'i durdur
      setTimeout(() => { clearInterval(poll); setPetrolUpdating(false) }, 720000)
    } catch (e) {
      setPetrolUpdating(false)
      alert('Güncelleme başlatılamadı: ' + (e?.message || ''))
    }
  }

  const handleCrossFilter = (widgetId, column, value) => {
    const page = dashboard?.pages[activePage]
    if (!page) return

    // Widget'in interaction_mode'unu kontrol et
    const widget = page.widgets.find(w => w.id === widgetId)
    const mode = widget?.config?.interaction_mode || 'filter'
    if (mode === 'none') return  // Bu widget baskalarini etkilemez

    // Ayni filtreye tekrar tiklanirsa kapat (toggle)
    if (crossFilterSource?.widgetId === widgetId && crossFilterSource?.column === column && crossFilterSource?.value === value) {
      setCrossFilterSource(null)
      setActiveFilters({})
      loadPageData(page, {})
      return
    }

    setCrossFilterSource({ widgetId, column, value })
    const newFilters = { ...activeFilters, [column]: value }
    // DONEM/AY/YIL/GUN gibi tarih filtrelerini tarih araligi olarak da ekle
    const strVal = String(value || '').trim()
    // Turkce ay kisaltmalari -> ay numarasi
    const TR_AY = {
      'oca':1,'ocak':1,'şub':2,'sub':2,'subat':2,'şubat':2,
      'mar':3,'mart':3,'nis':4,'nisan':4,'may':5,'mayıs':5,'mayis':5,
      'haz':6,'haziran':6,'tem':7,'temmuz':7,'ağu':8,'agu':8,'ağustos':8,'agustos':8,
      'eyl':9,'eylül':9,'eylul':9,'eki':10,'ekim':10,
      'kas':11,'kasım':11,'kasim':11,'ara':12,'aralık':12,'aralik':12,
    }
    const setRange = (y, m) => {
      const lastDay = new Date(y, m, 0).getDate()
      newFilters['_date_from'] = `${y}-${String(m).padStart(2,'0')}-01`
      newFilters['_date_to'] = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`
    }
    let mYM = /^(\d{4})-(\d{2})$/.exec(strVal)
    let mTr = /^([A-Za-zÇŞĞÜÖİçşğüöı]+)\s+(\d{4})$/.exec(strVal)  // "Ara 2025"
    let mTr2 = /^(\d{4})\s+([A-Za-zÇŞĞÜÖİçşğüöı]+)$/.exec(strVal)  // "2025 Ara"
    let mDay = /^\d{4}-\d{2}-\d{2}$/.test(strVal)
    let mYIL = column === 'YIL' && /^\d{4}$/.test(strVal)
    if (mYM) {
      setRange(parseInt(mYM[1]), parseInt(mYM[2]))
    } else if (mTr || mTr2) {
      const ayKey = (mTr ? mTr[1] : mTr2[2]).toLowerCase()
      const yil = parseInt(mTr ? mTr[2] : mTr2[1])
      const ay = TR_AY[ayKey]
      if (ay) setRange(yil, ay)
    } else if (mDay) {
      newFilters['_date_from'] = strVal
      newFilters['_date_to'] = strVal
    } else if (mYIL) {
      newFilters['_date_from'] = `${strVal}-01-01`
      newFilters['_date_to'] = `${strVal}-12-31`
    }
    setActiveFilters(newFilters)
    loadPageData(page, newFilters)
  }

  const setFilter = (column, value) => {
    const newFilters = { ...activeFilters }
    // null/boş string → filtreyi kaldır (tümü). Boş ARRAY → "hiçbiri seçili" olarak KORU (Hepsini Kaldır).
    if (value === '' || value == null) {
      delete newFilters[column]
    } else {
      newFilters[column] = value
    }
    setActiveFilters(newFilters)
    if (dashboard?.pages[activePage]) {
      loadPageData(dashboard.pages[activePage], newFilters)
    }
  }

  const setDateFilter = () => {
    const newFilters = { ...activeFilters }
    // Tek taraf secilirse digeri otomatik: baslangic var + bitis yok -> bugune kadar;
    // bitis var + baslangic yok -> ilk kayittan itibaren (kullanici karari)
    const _bugun = () => {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
    const efFrom = dateFrom || (dateTo ? '2000-01-01' : '')
    const efTo = dateTo || (dateFrom ? _bugun() : '')
    if (efFrom) newFilters['_date_from'] = efFrom
    else delete newFilters['_date_from']
    if (efTo) newFilters['_date_to'] = efTo
    else delete newFilters['_date_to']
    setActiveFilters(newFilters)
    if (dashboard?.pages[activePage]) {
      loadPageData(dashboard.pages[activePage], newFilters)
    }
  }

  // Genel Özet üst tarih filtresi: from/to alır, state + filters günceller, sayfayı yeniden yükler
  const _isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const applyDateRange = (from, to) => {
    setDateFrom(from || ''); setDateTo(to || '')
    const nf = { ...activeFilters }
    if (from) nf['_date_from'] = from; else delete nf['_date_from']
    if (to) nf['_date_to'] = to; else delete nf['_date_to']
    setActiveFilters(nf)
    if (dashboard?.pages[activePage]) loadPageData(dashboard.pages[activePage], nf)
  }
  const _AY_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
  const datePresets = (() => {
    const now = new Date(); const y = now.getFullYear(); const m = now.getMonth()
    const L = [
      { key: '', label: 'Hazır dönem…', from: '', to: '' },
      { key: 'tum', label: 'Tümü', from: '', to: '' },
      { key: 'buay', label: 'Bu Ay', from: _isoD(new Date(y, m, 1)), to: _isoD(new Date(y, m + 1, 0)) },
      { key: 'gecenay', label: 'Geçen Ay', from: _isoD(new Date(y, m - 1, 1)), to: _isoD(new Date(y, m, 0)) },
      { key: 'son3', label: 'Son 3 Ay', from: _isoD(new Date(y, m - 2, 1)), to: _isoD(now) },
      { key: 'buyil', label: 'Bu Yıl', from: _isoD(new Date(y, 0, 1)), to: _isoD(now) },
    ]
    for (let i = 0; i < 6; i++) {
      const d = new Date(y, m - i, 1); const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
      L.push({ key: `ay_${d.getFullYear()}_${d.getMonth()}`, label: `${_AY_TR[d.getMonth()]} ${d.getFullYear()}`, from: _isoD(d), to: _isoD(last) })
    }
    return L
  })()

  const clearFilters = () => {
    setActiveFilters({})
    setCrossFilterSource(null)
    setDateFrom('')
    setDateTo('')
    if (dashboard?.pages[activePage]) {
      loadPageData(dashboard.pages[activePage], {})
    }
  }

  const handleExport = async (format) => {
    const page = dashboard?.pages[activePage]
    if (!page) return
    const firstWidget = page.widgets.find(w => w.variable_id)
    if (!firstWidget) return
    try {
      const response = await api.get(`/export/${format}/${firstWidget.variable_id}`, {
        responseType: 'blob',
        params: Object.keys(activeFilters).length > 0 ? { filters: JSON.stringify(activeFilters) } : {},
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const a = document.createElement('a'); a.href = url
      a.download = `${dashboard.name}.${format === 'excel' ? 'xlsx' : 'pdf'}`
      a.click(); window.URL.revokeObjectURL(url)
    } catch (err) { toast.error('Export basarisiz') }
  }

  const switchPage = (index) => {
    setActivePage(index)
    // Filtreler korunuyor — tum sayfalara tasinir, istemezsen X ile kapat
    setCrossFilterSource(null)
    setWidgetData({}); setWidgetDataAlt({}); setWidgetDataTat({})
    buildLayouts(dashboard.pages[index])
    // Karlılık sayfası açılınca maliyet yöntemi otomatik "Ortalama Ağırlıklı" (FIFO kalıntısını ez)
    const _pn = dashboard.pages[index]?.name || ''
    if (_pn === 'Karlilik' && costMethod !== 'agirlikli') setCostMethod('agirlikli')
    if (_pn.includes('Personel') && !dateFrom && !dateTo) {
      const bu = datePresets.find(p => p.key === 'buay')
      const nf = { ...activeFilters, _date_from: bu.from, _date_to: bu.to }
      setDateFrom(bu.from); setDateTo(bu.to); setActiveFilters(nf)
      loadPageData(dashboard.pages[index], nf)
    } else {
      loadPageData(dashboard.pages[index], activeFilters)
    }
  }

  const handleLayoutChange = async (layout) => {
    if (!editMode) return
    const updates = layout.map(l => ({ id: parseInt(l.i), x: l.x, y: l.y, w: l.w, h: l.h }))
    try {
      await api.put('/widgets/batch-layout', { layouts: updates })
      toast.success('Duzen kaydedildi', { id: 'layout-save', duration: 1500 })
    } catch (e) {
      toast.error('Kaydedilemedi! Tekrar deneyin', { id: 'layout-save' })
    }
  }

  // Widget bazli export: format = 'xlsx' | 'pdf', mode = 'download' | 'share' (WhatsApp vs.)
  const exportWidget = async (widget, wData, format, mode = 'download') => {
    const rows = wData?.data || []
    if (!rows.length) { toast.error('Veri yok'); return }
    const title = widgetTitle(widget)
    const safeName = title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').substring(0, 50) || 'rapor'
    try {
      const token = localStorage.getItem('token')
      const resp = await fetch(`/api/export/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ data: rows, filename: safeName, title })
      })
      if (!resp.ok) { toast.error('Export hatasi'); return }
      const blob = await resp.blob()
      const ext = format === 'xlsx' ? 'xlsx' : 'pdf'
      const mime = format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf'
      const fname = `${safeName}.${ext}`
      // Paylas modu — Web Share API (mobil: WhatsApp dahil)
      if (mode === 'share' && navigator.canShare) {
        const file = new File([blob], fname, { type: mime })
        if (navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], title, text: title }); return } catch (e) { /* iptal/hata -> indir fallback */ }
        }
      }
      // Indir
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = fname; a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error('Paylasim/indirme basarisiz')
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>
  }
  if (!dashboard) return <div className="flex items-center justify-center h-full text-gray-400">Dashboard bulunamadi</div>

  const currentPage = dashboard.pages[activePage]
  const hasFilters = Object.keys(activeFilters).length > 0
  const filterColumns = Object.keys(filterOptions)
  // Musteri Takip sayfasi: SEFER_SAYISI kolonlu tablo iceren sayfa (YI/YD)
  const isMtakip = !!currentPage?.widgets?.some(
    w => Array.isArray(w.config?.columns) && w.config.columns.includes('SEFER_SAYISI'))
  // Widget verisini dondururken Musteri Takip tablosuna sefer sayisi / son sefer gun
  // araligi filtresini (client-side) uygula. 0 seferli satirlar zaten backend'de elenir.
  const wDataFor = (widget) => {
    const raw = widgetData[String(widget.id)] || widgetData[widget.id]
    const cols = widget.config?.columns || []
    if (!raw || !Array.isArray(raw.data) || widget.widget_type !== 'table' || !cols.includes('SEFER_SAYISI'))
      return raw
    const smin = mtSeferMin !== '' ? Number(mtSeferMin) : null
    const smax = mtSeferMax !== '' ? Number(mtSeferMax) : null
    const gmin = mtGunMin !== '' ? Number(mtGunMin) : null
    const gmax = mtGunMax !== '' ? Number(mtGunMax) : null
    if (smin == null && smax == null && gmin == null && gmax == null) return raw
    const data = raw.data.filter(r => {
      const s = Number(r.SEFER_SAYISI || 0)
      const g = (r.SON_SEFER_GUN == null || r.SON_SEFER_GUN === '') ? null : Number(r.SON_SEFER_GUN)
      if (smin != null && s < smin) return false
      if (smax != null && s > smax) return false
      if (gmin != null && (g == null || g < gmin)) return false
      if (gmax != null && (g == null || g > gmax)) return false
      return true
    })
    return { ...raw, data, row_count: data.length }
  }
  // Tank doluluk widget'i (detay) — varsa sayfayı grid yerine tam genişlik TankView ile göster
  const tankPageWidget = currentPage?.widgets?.find(w => w.config?._tank_data && !w.config?._tank_summary)

  // Sayfa sekmeleri JSX'i bir kez tanimla, mobile/desktop sirasi farkli
  const pageTabsEl = dashboard.pages.length > 1 ? (
    <div className={
      isMobile
        ? "bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-2 py-1.5 flex gap-1.5 flex-shrink-0 overflow-x-auto scrollbar-hide"
        : "bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-3 md:px-5 flex gap-0.5 flex-shrink-0 overflow-x-auto"
    }>
      {dashboard.pages.map((page, i) => (
        <button key={page.id} onClick={() => switchPage(i)}
          className={isMobile
            ? `px-3 py-1.5 text-xs rounded-full whitespace-nowrap flex-shrink-0 transition-colors border ${i === activePage ? 'bg-primary-600 text-white border-primary-600' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600'}`
            : `px-3 py-1.5 text-xs border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${i === activePage ? 'border-primary-600 text-primary-600 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`
          }>
          {page.name}
        </button>
      ))}
    </div>
  ) : null

  return (
    <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Mobil: sayfa sekmeleri EN USTTE */}
      {isMobile && pageTabsEl}
      {/* Header */}
      <div className={`bg-white dark:bg-gray-800 dark:text-gray-100 border-b dark:border-gray-700 px-3 md:px-5 py-2 flex items-center justify-between flex-shrink-0 gap-2 flex-wrap ${isMobile ? 'relative' : ''}`}>
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-base font-semibold text-gray-800 dark:text-gray-100">{dashboard.name}</h1>
            {dashboard.description && <p className="text-[11px] text-gray-400">{dashboard.description}</p>}
          </div>
          {hasFilters && (
            <div className="flex items-center gap-1 ml-3">
              {Object.entries(activeFilters).filter(([k]) => !k.startsWith('_')).map(([col, val]) => (
                <span key={col} className="inline-flex items-center gap-1 text-[10px] bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full border border-primary-200">
                  {col}: {Array.isArray(val) ? `${val.length} secili` : String(val).substring(0, 20)}
                  <button onClick={() => setFilter(col, null)} className="hover:text-red-500"><FiX size={9} /></button>
                </span>
              ))}
              {(dateFrom || dateTo) && (
                <span className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200">
                  {dateFrom || '...'} ~ {dateTo || '...'}
                </span>
              )}
              <button onClick={clearFilters} className="text-[10px] text-red-500 hover:text-red-700 ml-1">Temizle</button>
            </div>
          )}
        </div>
        <div className={`items-center gap-1.5 flex-wrap ${isMobile ? (toolsOpen ? 'flex w-full' : 'hidden') : 'flex'}`}>
          {/* Dinamik quick filter - dashboard'a gore otomatik tespit */}
          {(() => {
            // Musteri Raporu / Bakiye Raporu: urun filtresi ve Miktar-Tutar bu sayfalarda anlamsiz (kullanici karari)
            const _pgAd = dashboard?.pages?.[activePage]?.name || ''
            if (dashboard?.id === 8 && ['Musteri Raporu', 'Bakiye Raporu', 'Ödeme Yapmayanlar', 'Tank Doluluk', 'Karlilik'].includes(_pgAd)) return null
            // Sayfa ayarindan (layout_config.gizle) — orn. Aksantas Bakiye/Musteri/Tahsilat sayfalari
            if ((dashboard?.pages?.[activePage]?.gizle || []).includes('hizli_filtre')) return null
            const fuelVals = ['MOTORİN','KURŞUNSUZ BENZİN','LPG','MOTORIN','BENZIN','AUTOLPG']
            const isPetrolLike = filterOptions['URUN'] && filterOptions['URUN'].length > 0 && filterOptions['URUN'].length <= 8
              && filterOptions['URUN'].some(v => fuelVals.some(f => String(v||'').toUpperCase().includes(f)))
            const grupCol = filterOptions['STOKGRUBU'] ? 'STOKGRUBU'
                          : filterOptions['GRUP'] ? 'GRUP'
                          : filterOptions['GRUPADI'] ? 'GRUPADI'
                          : filterOptions['KATEGORI'] ? 'KATEGORI' : null
            const hasStokadi = !!filterOptions['STOKADI']
            // Pocan Market: grup varsa combobox (stokadi yoksa sadece grup gosterilir)
            if (grupCol && !isPetrolLike && (filterOptions[grupCol] || []).length > 4) {
              // Grup -> Urun map'i widgetData'dan cikar
              const grupUrunMap = {}
              Object.values(widgetData).forEach(wd => {
                if (!wd?.data) return
                wd.data.forEach(row => {
                  const g = row[grupCol]
                  const u = row.STOKADI
                  if (g && u) {
                    if (!grupUrunMap[g]) grupUrunMap[g] = new Set()
                    grupUrunMap[g].add(u)
                  }
                })
              })
              const selectedGrup = activeFilters[grupCol] || ''
              const allGruplar = (filterOptions[grupCol] || []).slice().sort()
              const urunler = selectedGrup
                ? [...(grupUrunMap[selectedGrup] || [])].sort()
                : (filterOptions['STOKADI'] || []).slice().sort()
              const selectedUrun = activeFilters['STOKADI'] || ''
              return (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <label className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Grup:</label>
                    <select value={selectedGrup}
                      onChange={e => { setFilter(grupCol, e.target.value); if (e.target.value && selectedUrun) setFilter('STOKADI', '') }}
                      className="text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-200 cursor-pointer min-w-[8rem]">
                      <option value="">Hepsi ({allGruplar.length})</option>
                      {allGruplar.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  {hasStokadi && (
                    <div className="flex items-center gap-1">
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">Ürün:</label>
                      <select value={selectedUrun} onChange={e => setFilter('STOKADI', e.target.value)}
                        className="text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-200 cursor-pointer min-w-[12rem] max-w-[20rem]">
                        <option value="">Hepsi ({urunler.length})</option>
                        {urunler.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )
            }
            const quickCol = isPetrolLike ? 'URUN' : grupCol
            if (!quickCol) return null
            const rawVals = filterOptions[quickCol] || []
            const vals = rawVals.slice(0, 6)
            const shortLabel = (v) => {
              const s = String(v||'')
              if (s.toUpperCase().includes('MOTORİN') || s.toUpperCase().includes('MOTORIN')) return 'Motorin'
              if (s.toUpperCase().includes('BENZİN') || s.toUpperCase().includes('BENZIN')) return 'Benzin'
              if (s.toUpperCase() === 'LPG' || s.toUpperCase() === 'AUTOLPG') return 'LPG'
              return s.length > 10 ? s.substring(0, 10) : s
            }
            return (
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 flex-wrap">
                {[{ key: '', label: 'Hepsi' }, ...vals.map(v => ({ key: v, label: shortLabel(v) }))].map(f => {
                  const isActive = (activeFilters[quickCol] || '') === f.key
                  return (
                    <button key={f.label} onClick={() => setFilter(quickCol, f.key)}
                      title={f.key || 'Tumu'}
                      className={`text-xs px-2.5 py-1.5 font-medium transition-colors ${isActive ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                      {f.label}
                    </button>
                  )
                })}
              </div>
            )
          })()}
          {/* Lojistik (11/14): Miktar/Tutar'ın işi yok -> yerine Özmal / Taşeron / Tümü (araç tipi) seçici.
              Cari ağırlıklı sayfalarda (kasa/bakiye/ödeme/vade) araç filtresi anlamsız -> gizle. */}
          {[11, 14].includes(dashboard?.id) ? (
            !['Musteri Kasa Hareketleri', 'Bakiye Raporu', 'Ödeme Yapmayanlar', 'Vade Takip', 'Minivan Araclar'].includes(dashboard?.pages?.[activePage]?.name || '') && (
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              {[['', 'Tümü'], ['1', 'Özmal'], ['2', 'Taşeron']].map(([k, l]) => (
                <button key={k} onClick={() => setAracTipi(k)}
                  className={`text-xs px-3 py-1.5 font-medium transition-colors ${aracTipi === k ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-200'}`}>
                  {l}
                </button>
              ))}
            </div>
            )
          ) : !(
            (dashboard?.id === 8 && ['Musteri Raporu', 'Bakiye Raporu', 'Ödeme Yapmayanlar', 'Tank Doluluk', 'Karlilik'].includes(dashboard?.pages?.[activePage]?.name || ''))
            || ([10, 12].includes(dashboard?.id) && (dashboard?.pages?.[activePage]?.name || '') === 'Karlilik')
            || (dashboard?.pages?.[activePage]?.gizle || []).includes('miktar_tutar')
          ) && (
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <button onClick={() => setShowMode('miktar')}
                className={`text-xs px-3 py-1.5 font-medium transition-colors ${showMode === 'miktar' ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-200'}`}>
                Miktar
              </button>
              <button onClick={() => setShowMode('tutar')}
                className={`text-xs px-3 py-1.5 font-medium transition-colors ${showMode === 'tutar' ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-200'}`}>
                Tutar
              </button>
            </div>
          )}
          {/* Yurtdışı: araç türü (Çekici / Dorse / Minivan) filtresi — minivanları ayrı görebilmek için */}
          {dashboard?.id === 14 && (dashboard?.pages?.[activePage]?.name || '') !== 'Minivan Araclar' && (
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              {[['', 'Tüm Araç'], ['1', 'Çekici'], ['2', 'Dorse'], ['3', 'Minivan']].map(([k, l]) => (
                <button key={k} onClick={() => setAracTuru(k)}
                  className={`text-xs px-3 py-1.5 font-medium transition-colors ${aracTuru === k ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-200'}`}>
                  {l}
                </button>
              ))}
            </div>
          )}
          {/* Para birimi modu — Döviz (€) vs TL Karşılığı (her işlem tutar×kur ile TL'ye çevrilir).
              qr22: eskiden yalnız pano 14'ün birkaç sayfasındaydı; döviz geçen TÜM lojistik
              sayfalarında (yurtiçi + yurtdışı, tüm sekmeler) görünüyor. */}
          {[11, 14].includes(dashboard?.id) && (
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              {/* Etiketler bilerek acik: "€" tusu CEVIRMEZ, her tutari kendi para biriminde
                  gosterir; "₺ karsiligi" doviz tutarlari ISLEM KURUYLA ₺'ye cevirir.
                  Ters yon (₺ -> €) hic yapilmaz, o yuzden "kuru yok" durumu olusmaz. */}
              {[['', '₺/€ ayrı', 'Her tutar kendi para biriminde gösterilir (çevrim yapılmaz)'],
                ['tl', '₺ karşılığı', 'Döviz tutarlar kendi işlem kuruyla ₺ karşılığına çevrilir']].map(([k, l, ipucu]) => (
                <button key={k} onClick={() => setCurrencyMode(k)} title={ipucu}
                  className={`text-xs px-3 py-1.5 font-medium transition-colors ${currencyMode === k ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-200'}`}>
                  {l}
                </button>
              ))}
            </div>
          )}
          {/* Genel Özet (lojistik yurtiçi+yurtdışı) için üst tarih filtresi: başlangıç–bitiş + hazır dönem listesi */}
          {(([11, 14].includes(dashboard?.id) && (['Genel Ozet', 'Aylik Kar Analizi', 'Bakiye Raporu', 'Musteri Kasa Hareketleri', 'Arac Detay'].includes(dashboard?.pages?.[activePage]?.name || '') || (dashboard?.pages?.[activePage]?.name || '').includes('Personel')))
            || ([8, 10, 12].includes(dashboard?.id) && (dashboard?.pages?.[activePage]?.name || '') === 'Karlilik')) && (
            <div className="flex flex-col gap-1 bg-primary-50 dark:bg-gray-800 rounded-lg border border-primary-200 dark:border-gray-700 px-2 py-1">
              <div className="flex items-center gap-1">
                <span className="text-[11px] font-semibold text-primary-700 dark:text-primary-300 whitespace-nowrap">📅 Tarih:</span>
                <input type="date" value={dateFrom} max={dateTo || undefined}
                  onChange={(e) => applyDateRange(e.target.value, dateTo)}
                  className="text-[11px] border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:text-gray-200" />
                <span className="text-gray-400 text-xs">–</span>
                <input type="date" value={dateTo} min={dateFrom || undefined}
                  onChange={(e) => applyDateRange(dateFrom, e.target.value)}
                  className="text-[11px] border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:text-gray-200" />
                {(dateFrom || dateTo) && (
                  <button onClick={() => applyDateRange('', '')} title="Tarihi temizle"
                    className="text-gray-400 hover:text-red-500 text-xs px-0.5 font-bold">✕</button>
                )}
              </div>
              <select value=""
                onChange={(e) => { const p = datePresets.find(x => x.key === e.target.value); if (p) applyDateRange(p.from, p.to) }}
                className="text-[11px] border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-900 dark:text-gray-200">
                {datePresets.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
          )}
          <button onClick={() => setFilterPanelOpen(!filterPanelOpen)}
            className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${filterPanelOpen ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
            <FiFilter size={12} /> Filtreler {hasFilters && <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />}
          </button>
          <button onClick={handleRefresh} disabled={refreshing}
            className={`p-1.5 rounded-lg transition-colors ${refreshing ? 'text-primary-400' : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-gray-800'}`}>
            <FiRefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
          {/* Pocan Petrol / Market: Firebird'den verileri yeniden çek (Quark'ta düzeltme sonrası) */}
          {(dashboard?.id === 8 || dashboard?.id === 10) && (
            <button onClick={updatePetrolData} disabled={petrolUpdating}
              title="Quark/Firebird'de fatura/veri düzelttiysen bas — verileri kaynaktan yeniden çeker (birkaç dk sürer)"
              className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${petrolUpdating ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
              <FiRefreshCw size={12} className={petrolUpdating ? 'animate-spin' : ''} />
              {petrolUpdating ? 'Güncelleniyor…' : 'Verileri Güncelle'}
            </button>
          )}
          <div className="relative group">
            <button className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-gray-800 rounded-lg"><FiDownload size={14} /></button>
            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 hidden group-hover:block z-10 min-w-[100px]">
              <button onClick={() => handleExport('excel')} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700">Excel</button>
              <button onClick={() => handleExport('pdf')} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700">PDF</button>
            </div>
          </div>
          {/* Pocan Petrol icin sube secimi */}
          {dashboard?.name === 'Pocan Petrol' && (
            <select value={pocanSube} onChange={(e) => setPocanSube(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              title="Şube seçimi">
              <option value="altinekin">Altınekin</option>
              <option value="tatlicak">Tatlıcak</option>
              <option value="ladik">Ladık</option>
              <option value="tum">Tümü (3 Şube)</option>
            </select>
          )}
          {/* Karlilik sayfasinda maliyet yontemi combobox */}
          {dashboard?.pages?.[activePage]?.name?.toLowerCase().includes('karli') && (
            <select value={costMethod} onChange={(e) => setCostMethod(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              title="Maliyet hesaplama yontemi">
              <option value="en_son">En Son Alis Fiyati</option>
              <option value="ortalama">Ortalama Alis Fiyati</option>
              <option value="agirlikli">Ortalama Agirlikli Alis Fiyati</option>
              <option value="en_ucuz">En Ucuz Alis Fiyati</option>
              <option value="en_pahali">En Pahali Alis Fiyati</option>
              <option value="fifo">FIFO</option>
            </select>
          )}
          {/* Ayarlar - cark menusu (en sagda) */}
          <div className="relative" ref={settingsRef}>
            <button onClick={() => setSettingsMenuOpen(!settingsMenuOpen)}
              title="Goruntu ayarlari"
              className="flex items-center text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <FiSettings size={13} />
            </button>
            {settingsMenuOpen && (
              <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 z-50 w-56 text-xs p-3 space-y-3">
                <div>
                  <div className="font-medium text-gray-500 dark:text-gray-400 mb-1.5 text-[10px] uppercase tracking-wide">Tema</div>
                  <div className="flex bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <button onClick={() => setTheme('light')} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 font-medium transition-colors ${theme === 'light' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}><FiSun size={11} /> Acik</button>
                    <button onClick={() => setTheme('dark')} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 font-medium transition-colors ${theme === 'dark' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}><FiMoon size={11} /> Koyu</button>
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-500 dark:text-gray-400 mb-1.5 text-[10px] uppercase tracking-wide">Sayi Gorunumu</div>
                  <div className="flex bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <button onClick={() => setNumberView('short')} className={`flex-1 px-2 py-1.5 font-medium transition-colors ${numberView === 'short' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>Kisa</button>
                    <button onClick={() => setNumberView('all')} className={`flex-1 px-2 py-1.5 font-medium transition-colors ${numberView === 'all' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>Tum</button>
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-500 dark:text-gray-400 mb-1.5 text-[10px] uppercase tracking-wide">Tablo Sutun Boyutu</div>
                  <div className="flex bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    {[{k:'sm',l:'Dar'},{k:'md',l:'Normal'},{k:'lg',l:'Genis'}].map(o => (
                      <button key={o.k} onClick={() => setTableColSize(o.k)} className={`flex-1 px-2 py-1.5 font-medium transition-colors ${tableColSize === o.k ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-500 dark:text-gray-400 mb-1.5 text-[10px] uppercase tracking-wide flex items-center justify-between">
                    <span>Tablo Yazı Boyutu</span>
                    <span className="text-primary-600 dark:text-primary-400 font-bold text-xs tabular-nums">{tableFontPx}px</span>
                  </div>
                  <input type="range" min="9" max="24" step="1" value={tableFontPx}
                    onChange={(e) => setTableFontPx(Number(e.target.value))}
                    className="w-full accent-primary-600 cursor-pointer" />
                  <div className="flex justify-between text-[9px] text-gray-400 mt-0.5"><span>Küçük</span><span>Büyük</span></div>
                </div>
                <div>
                  <div className="font-medium text-gray-500 dark:text-gray-400 mb-1.5 text-[10px] uppercase tracking-wide">Renk Paleti</div>
                  <div className="space-y-0.5">
                    {[
                      {key:'default', name:'Varsayilan', colors:['#3b82f6','#10b981','#f59e0b','#ef4444']},
                      {key:'vivid',   name:'Canli',      colors:['#dc2626','#2563eb','#16a34a','#ca8a04']},
                      {key:'pastel',  name:'Pastel',     colors:['#93c5fd','#86efac','#fcd34d','#fca5a5']},
                      {key:'mono',    name:'Mavi Mono',  colors:['#1e3a8a','#2563eb','#60a5fa','#bfdbfe']},
                      {key:'warm',    name:'Sicak',      colors:['#dc2626','#ea580c','#f59e0b','#eab308']},
                      {key:'cool',    name:'Soguk',      colors:['#0ea5e9','#14b8a6','#6366f1','#8b5cf6']},
                    ].map(p => (
                      <button key={p.key} onClick={() => { setColorPalette(p.key); if (dashboard?.pages[activePage]) loadPageData(dashboard.pages[activePage], activeFilters) }}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${colorPalette === p.key ? 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <span className="inline-flex gap-0.5">{p.colors.map((c,i) => <span key={i} className="w-2 h-2 rounded-full" style={{background:c}} />)}</span>
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
                {user?.is_admin && (
                  <div className="pt-2 border-t dark:border-gray-700 space-y-1">
                    <button onClick={() => { setSettingsMenuOpen(false); navigate(`/designer/${id}`) }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <FiEdit size={11} /> Sayfayi Duzenle
                    </button>
                    <button onClick={() => setEditMode(!editMode)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded ${editMode ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                      {editMode ? <FiUnlock size={11} /> : <FiLock size={11} />}
                      {editMode ? 'Serbest (Duzenleme acik)' : 'Duzenlemeyi Ac'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Mobil: alt-orta chevron — banner tarzi yukari toplama / asagi acma */}
        {isMobile && (
          <button onClick={() => setToolsOpen(!toolsOpen)}
            aria-label={toolsOpen ? 'Aractlari gizle' : 'Aractlari goster'}
            className="md:hidden absolute left-1/2 -translate-x-1/2 -bottom-3 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow-md w-9 h-6 flex items-center justify-center text-gray-500 hover:text-primary-600">
            <FiChevronUp size={14} className={`transition-transform ${toolsOpen ? '' : 'rotate-180'}`} />
          </button>
        )}
      </div>

      {/* Masaustu: Sayfa sekmeleri header'in altinda (mobile'da yukari tasinmis) */}
      {!isMobile && pageTabsEl}

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Widget Canvas — desktop'ta sığmazsa içerik orantılı küçülür (zoom) */}
        <div ref={canvasRef} className="flex-1 overflow-auto p-3">
          {/* Sayfa açıklaması — bu sayfada ne olduğunu kısaca anlatır */}
          {currentPage?.description && (
            <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 text-[12px] text-blue-800 dark:text-blue-200">
              <FiInfo size={14} className="mt-0.5 flex-shrink-0 text-blue-500" />
              <span>{currentPage.description}</span>
            </div>
          )}
          {/* Musteri Takip: sefer sayisi / son sefer gun araligi filtresi */}
          {isMtakip && (
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-[12px]">
              <span className="font-medium text-gray-600 dark:text-gray-300">Filtrele:</span>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-500">Sefer sayısı</span>
                <input type="number" min="0" value={mtSeferMin} onChange={e => setMtSeferMin(e.target.value)} placeholder="min"
                  className="w-16 px-2 py-1 border border-slate-300 dark:border-gray-600 rounded dark:bg-gray-700 tabular-nums" />
                <span className="text-gray-400">–</span>
                <input type="number" min="0" value={mtSeferMax} onChange={e => setMtSeferMax(e.target.value)} placeholder="max"
                  className="w-16 px-2 py-1 border border-slate-300 dark:border-gray-600 rounded dark:bg-gray-700 tabular-nums" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-500">Son sefer (gün)</span>
                <input type="number" min="0" value={mtGunMin} onChange={e => setMtGunMin(e.target.value)} placeholder="min"
                  className="w-16 px-2 py-1 border border-slate-300 dark:border-gray-600 rounded dark:bg-gray-700 tabular-nums" />
                <span className="text-gray-400">–</span>
                <input type="number" min="0" value={mtGunMax} onChange={e => setMtGunMax(e.target.value)} placeholder="max"
                  className="w-16 px-2 py-1 border border-slate-300 dark:border-gray-600 rounded dark:bg-gray-700 tabular-nums" />
              </div>
              {(mtSeferMin || mtSeferMax || mtGunMin || mtGunMax) ? (
                <button onClick={() => { setMtSeferMin(''); setMtSeferMax(''); setMtGunMin(''); setMtGunMax('') }}
                  className="text-red-500 hover:underline">temizle</button>
              ) : null}
            </div>
          )}
          <div style={(!isMobile && !editMode && fitZoom < 1) ? { zoom: fitZoom } : undefined}>
          {currentPage && tankPageWidget ? (
            /* Tank doluluk: tüm sayfa genişliğinde maildeki düzen (grid değil) */
            <TankView data={widgetData[String(tankPageWidget.id)]?.data || widgetData[tankPageWidget.id]?.data || []} />
          ) : currentPage && layouts.lg ? (
            isMobile ? (
              <div className="flex flex-col gap-3 pb-4">
                {currentPage.widgets.map((widget) => {
                  const wData = wDataFor(widget)
                  const effectiveConfig = isCompareTum(currentPage)
                    ? { ...(widget.config || {}), color_column: 'ISTASYON' }
                    : (widget.config || {})
                  // Mobil sabit yukseklik: KPI 110px, harita 360px, tablo 380px, grafik 280px
                  const wtype = widget.widget_type
                  const heightPx = wtype === 'kpi' ? 110
                                  : wtype === 'map' ? 360
                                  : (wtype === 'table' || wtype === 'pivot' || wtype === 'bakiye') ? 420
                                  : 280
                  return (
                    <div key={String(widget.id)} className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between px-3 py-1 border-b border-gray-50 dark:border-gray-700 flex-shrink-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-xs font-medium text-gray-600 truncate">{widgetTitle(widget)}</span>
                          {wData?.row_count != null && <span className="text-[9px] text-gray-300">({wData.row_count})</span>}
                          {wData?.snapshot_time && (
                            <span title={`Son guncelleme: ${new Date(wData.snapshot_time).toLocaleString('tr-TR')}`}
                                  className="text-[9px] text-gray-400 ml-0.5">
                              {new Date(wData.snapshot_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {widget.config?.info && (
                            <button onClick={() => setInfoWidget({title: widgetTitle(widget), info: widget.config.info})}
                              title="Bu widget'in aciklamasi"
                              className="text-gray-400 hover:text-blue-600 p-1.5">
                              <FiInfo size={17} />
                            </button>
                          )}
                          <button onClick={() => exportWidget(widget, wData, 'xlsx', 'share')}
                            title="Excel paylas / indir" className="text-gray-400 hover:text-green-600 p-1.5">
                            <FiShare2 size={17} />
                          </button>
                          <button onClick={() => exportWidget(widget, wData, 'pdf', 'share')}
                            title="PDF paylas / indir" className="text-gray-400 hover:text-red-600 p-1.5">
                            <FiFileText size={17} />
                          </button>
                          <button onClick={() => setFullscreenWidget(fullscreenWidget === widget.id ? null : widget.id)} className="text-gray-300 hover:text-gray-500 p-0.5">
                            <FiMaximize size={11} />
                          </button>
                        </div>
                      </div>
                      <div style={{ height: `${heightPx}px` }} className="flex-shrink-0">
                        <ChartWidget type={widget.widget_type} data={wData?.data || []} stations={wData?.stations} config={effectiveConfig} title={widgetTitle(widget)} widgetId={widget.id}
                          dateFrom={dateFrom} dateTo={dateTo} showMode={showMode}
                          numberView={numberView} colorPalette={colorPalette} theme={theme} pocanSube={pocanSube} aracTipi={aracTipi} aracTuru={aracTuru} currencyMode={currencyMode}
                          tableColSize={tableColSize} tableFontSize={tableFontSize}
                          onCrossFilter={(column, value) => handleCrossFilter(widget.id, column, value)} />
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
            <ResponsiveGridLayout
              className="layout"
              layouts={layouts}
              breakpoints={{ lg: 1200, md: 996, sm: 768 }}
              cols={{ lg: 12, md: 12, sm: 6 }}
              rowHeight={80}
              isDraggable={editMode}
              isResizable={editMode}
              onLayoutChange={(layout) => handleLayoutChange(layout)}
              draggableHandle=".widget-drag-handle"
              compactType="vertical"
              margin={[8, 8]}
              useCSSTransforms={!fitZoom || fitZoom >= 1}
            >
              {currentPage.widgets.map((widget) => {
                const wData = wDataFor(widget)
                const isFilterSource = crossFilterSource?.widgetId === widget.id
                const isFiltered = crossFilterSource && !isFilterSource
                const interactionMode = widget.config?.interaction_mode || 'filter' // filter | highlight | none
                // Karsilastirma + Tum: iki sube ayni grafikte overlay (color_column=ISTASYON)
                const effectiveConfig = isCompareTum(currentPage)
                  ? { ...(widget.config || {}), color_column: 'ISTASYON' }
                  : (widget.config || {})
                return (
                  <div key={String(widget.id)} className={`bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-sm border overflow-hidden flex flex-col transition-all ${
                    isFilterSource ? 'border-primary-400 ring-2 ring-primary-200' : 'border-gray-100 dark:border-gray-700'
                  }`}>
                    {/* Cross-filter durum gostergesi */}
                    {isFiltered && interactionMode !== 'none' && (
                      <div className="bg-primary-50 px-2 py-0.5 text-[9px] text-primary-600 flex items-center gap-1 flex-shrink-0">
                        <FiFilter size={8} />
                        <span>{interactionMode === 'filter' ? 'Filtrelendi' : 'Vurgulandi'}: {crossFilterSource.column} = {String(crossFilterSource.value).substring(0, 20)}</span>
                      </div>
                    )}
                    {isFilterSource && (
                      <div className="bg-primary-500 px-2 py-0.5 text-[9px] text-white flex items-center justify-between flex-shrink-0">
                        <span>Secili: {crossFilterSource.column} = {String(crossFilterSource.value).substring(0, 20)}</span>
                        <button onClick={(e) => { e.stopPropagation(); setCrossFilterSource(null); clearFilters() }} className="hover:text-red-200"><FiX size={10} /></button>
                      </div>
                    )}
                    <div className={`flex items-center justify-between px-3 py-1 border-b border-gray-50 dark:border-gray-700 flex-shrink-0 ${editMode ? 'widget-drag-handle cursor-move bg-gray-50 dark:bg-gray-700' : ''}`}>
                      <div className="flex items-center gap-1.5 min-w-0">
                        {editMode && <span className="text-gray-300 text-[10px]">::</span>}
                        <span className="text-[11px] font-medium text-gray-600 truncate">{widgetTitle(widget)}</span>
                        {wData?.row_count != null && <span className="text-[9px] text-gray-300">({wData.row_count})</span>}
                        {wData?.snapshot_time && (
                          <span title={`Son guncelleme: ${new Date(wData.snapshot_time).toLocaleString('tr-TR')}`}
                                className="text-[9px] text-gray-400 ml-0.5">
                            {new Date(wData.snapshot_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {widget.config?.info && (
                          <button onClick={(e) => { e.stopPropagation(); setInfoWidget({title: widgetTitle(widget), info: widget.config.info}) }}
                            title="Bu widget'in aciklamasi"
                            className="text-gray-400 hover:text-blue-600 p-1">
                            <FiInfo size={15} />
                          </button>
                        )}
                        <button onClick={() => exportWidget(widget, wData, 'xlsx', 'download')}
                          title="Excel indir" className="text-gray-400 hover:text-green-600 p-1">
                          <FiDownload size={15} />
                        </button>
                        <button onClick={() => exportWidget(widget, wData, 'pdf', 'download')}
                          title="PDF indir" className="text-gray-400 hover:text-red-600 p-1">
                          <FiFileText size={15} />
                        </button>
                        <button onClick={() => setFullscreenWidget(fullscreenWidget === widget.id ? null : widget.id)} className="text-gray-400 hover:text-gray-500 p-1">
                          <FiMaximize size={15} />
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 min-h-0">
                      <ChartWidget type={widget.widget_type} data={wData?.data || []} stations={wData?.stations} config={effectiveConfig} title={widgetTitle(widget)} widgetId={widget.id}
                        dashId={dashboard?.id}
                        dateFrom={dateFrom} dateTo={dateTo} showMode={showMode}
                        numberView={numberView} colorPalette={colorPalette} theme={theme} pocanSube={pocanSube} aracTipi={aracTipi} aracTuru={aracTuru} currencyMode={currencyMode}
                        tableColSize={tableColSize} tableFontSize={tableFontSize}
                        onCrossFilter={(column, value) => handleCrossFilter(widget.id, column, value)} />
                    </div>
                  </div>
                )
              })}
            </ResponsiveGridLayout>
            )
          ) : (
            <div className="text-center text-gray-400 py-20">Widget yok</div>
          )}
          </div>
          {/* Cari Detay Panel - cari secildiginde altta gozukur */}
          <CariDetayPanel />
        </div>

        {/* Filtre Paneli — mobilde ustten asagi slide, masaustunde yan panel */}
        {filterPanelOpen && isMobile && (
          <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setFilterPanelOpen(false)} />
        )}
        {filterPanelOpen && (
          <div className={isMobile
            ? "fixed inset-x-0 top-12 z-50 max-h-[75vh] bg-white dark:bg-gray-800 border-b dark:border-gray-700 flex flex-col flex-shrink-0 overflow-hidden shadow-2xl rounded-b-2xl animate-slide-down"
            : "w-64 bg-white border-l flex flex-col flex-shrink-0 overflow-hidden"
          }>
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">Filtreler</span>
              <button onClick={() => setFilterPanelOpen(false)} className="text-gray-400 hover:text-gray-600"><FiX size={14} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {/* Lojistik filtreleri */}
              {dashboard?.name?.toLowerCase().includes('lojistik') && (
                <div className="space-y-2">
                  <div className="text-[11px] font-medium text-gray-600">Lojistik Filtreleri</div>
                  <div>
                    <label className="text-[10px] text-gray-500">Yurt İçi / Yurt Dışı</label>
                    <select value={tasimaTuru} onChange={(e) => setTasimaTuru(e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                      <option value="">Tümü</option>
                      <option value="YI">Yurt İçi</option>
                      <option value="YD">Yurt Dışı</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Para Birimi</label>
                    <select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                      <option value="">Tüm Dövizler</option>
                      <option value="TL">TL (₺)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Sefer Durumu</label>
                    <select value={seferDurumu} onChange={(e) => setSeferDurumu(e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                      <option value="">Tüm Durumlar</option>
                      <option value="1">Taslak</option>
                      <option value="2">Devam Eden</option>
                      <option value="3">Tamamlanan</option>
                      <option value="4">İptal</option>
                      <option value="5">Faturalanmış</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Araç Tipi</label>
                    <select value={aracTipi} onChange={(e) => setAracTipi(e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                      <option value="">Tüm Araçlar</option>
                      <option value="1">Özmal</option>
                      <option value="2">Taşeron</option>
                    </select>
                  </div>
                  <div className="relative">
                    <label className="text-[10px] text-gray-500">Plaka</label>
                    <div className="relative">
                      <input
                        value={plakaAcik ? plakaQ : (plakaFilter || '')}
                        onFocus={() => { setPlakaQ(''); setPlakaAcik(true) }}
                        onBlur={() => setTimeout(() => setPlakaAcik(false), 150)}
                        onChange={(e) => { setPlakaQ(e.target.value); setPlakaAcik(true) }}
                        placeholder={plakaFilter ? plakaFilter : 'Plaka ara...'}
                        className="w-full px-2 py-1 pr-6 border rounded text-xs bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100" />
                      {plakaFilter && (
                        <button type="button" title="Plaka filtresini temizle"
                          onMouseDown={(e) => { e.preventDefault(); setPlakaFilter(''); setPlakaQ(''); setPlakaAcik(false) }}
                          className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600 text-xs px-1">✕</button>
                      )}
                    </div>
                    {plakaAcik && (() => {
                      const q = plakaQ.trim().toLocaleLowerCase('tr-TR').replace(/\s+/g, '')
                      const liste = plakaList.filter(p =>
                        !q || String(p.plaka || '').toLocaleLowerCase('tr-TR').replace(/\s+/g, '').includes(q))
                      return (
                        <div className="absolute left-0 right-0 top-full mt-0.5 z-50 max-h-64 overflow-auto rounded-md border dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg">
                          {liste.length === 0 && (
                            <div className="px-2 py-2 text-[11px] text-gray-400">Eşleşen plaka yok</div>
                          )}
                          {liste.slice(0, 200).map(p => (
                            <button key={p.plaka} type="button"
                              onMouseDown={(e) => { e.preventDefault(); setPlakaFilter(p.plaka); setPlakaQ(''); setPlakaAcik(false) }}
                              className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left text-xs hover:bg-blue-50 dark:hover:bg-blue-900/30 ${plakaFilter === p.plaka ? 'bg-blue-50 dark:bg-blue-900/30 font-medium' : ''}`}>
                              <span className="truncate dark:text-gray-100">{p.plaka}</span>
                              {(p.turu || p.tip) && (
                                <span className="flex-shrink-0 text-[10px] text-gray-500 dark:text-gray-400">
                                  {[p.turu, p.tip].filter(Boolean).join(' · ')}
                                </span>
                              )}
                            </button>
                          ))}
                          {liste.length > 200 && (
                            <div className="px-2 py-1 text-[10px] text-gray-400 border-t dark:border-gray-700">
                              {liste.length} sonuç — ilk 200 gösteriliyor, aramayı daraltın
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                  <div className="border-b pt-2" />
                </div>
              )}
              {/* Tarih Filtresi */}
              <div>
                <div className="flex items-center gap-1 mb-1.5">
                  <FiCalendar size={11} className="text-purple-500" />
                  <span className="text-[11px] font-medium text-gray-600">Tarih Araligi</span>
                </div>
                <div className="space-y-1.5">
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-2 py-1 border rounded text-xs" />
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-2 py-1 border rounded text-xs" />
                  <button onClick={setDateFilter} className="w-full text-[10px] bg-purple-50 text-purple-600 py-1 rounded hover:bg-purple-100">Uygula</button>
                </div>
              </div>

              {/* Dinamik Filtreler — widget verilerinden otomatik cikarilir */}
              {filterColumns.map(col => {
                const values = filterOptions[col]
                const currentVal = activeFilters[col]
                const search = filterSearch[col] || ''
                const filtered = search ? values.filter(v => v.toLowerCase().includes(search.toLowerCase())) : values

                return (
                  <FilterSection key={col} title={col} values={filtered} currentVal={currentVal}
                    search={search}
                    onSearch={(s) => setFilterSearch(prev => ({ ...prev, [col]: s }))}
                    onSelect={(val) => setFilter(col, val)}
                    onClear={() => setFilter(col, null)}
                  />
                )
              })}

              {filterColumns.length === 0 && (
                <p className="text-[11px] text-gray-400 text-center py-4">Filtre secenekleri veri yuklenince otomatik olusur</p>
              )}
            </div>

            {/* Temizle */}
            {hasFilters && (
              <div className="p-3 border-t">
                <button onClick={clearFilters} className="w-full text-xs text-red-500 py-1.5 border border-red-200 rounded-lg hover:bg-red-50">Tum Filtreleri Temizle</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Veriler çekilemedi uyarısı */}
      {veriSorunlari.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800">
              <FiAlertTriangle className="text-amber-500 shrink-0" size={22} />
              <div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Veriler çekilemedi</h3>
                <p className="text-xs text-gray-600 dark:text-gray-300">Aşağıdaki kaynaklardan güncel veri alınamıyor. Bu kaynakları kullanan tablolarda son alınan veriler gösteriliyor.</p>
              </div>
            </div>
            <ul className="px-5 py-3 max-h-[50vh] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {veriSorunlari.map((s, i) => (
                <li key={i} className="py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{s.ad}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Son başarılı: {_sonBasarili(s.son_basarili)}</span>
                  </div>
                  {s.hata && <div className="text-xs text-red-600 dark:text-red-400 mt-0.5 break-words">{s.hata}</div>}
                </li>
              ))}
            </ul>
            <div className="px-5 py-3 bg-gray-50 dark:bg-gray-900/40 flex justify-end">
              <button autoFocus onClick={() => setVeriSorunlari([])} className="px-6 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold">Tamam</button>
            </div>
          </div>
        </div>
      )}

      {/* Widget Aciklama Modal */}
      {infoWidget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setInfoWidget(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <FiInfo className="text-blue-500" size={18}/>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{infoWidget.title}</h3>
              </div>
              <button onClick={() => setInfoWidget(null)} className="text-gray-400 hover:text-gray-700 p-1"><FiX size={16}/></button>
            </div>
            <div className="p-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">
              {infoWidget.info}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen */}
      {fullscreenWidget && currentPage && (() => {
        const w = currentPage.widgets.find(w => w.id === fullscreenWidget)
        if (!w) return null
        const wData = wDataFor(w)
        return (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setFullscreenWidget(null)} />
            <div className={`fixed bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden ${isMobile ? 'inset-1' : 'inset-6'}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <span className="font-semibold text-gray-800">{widgetTitle(w)}</span>
                <button onClick={() => setFullscreenWidget(null)} className="p-1 hover:bg-gray-100 rounded"><FiMinimize size={16} /></button>
              </div>
              <div className="flex-1 p-4 overflow-auto">
                <ChartWidget type={w.widget_type} data={wData?.data || []} config={w.config || {}} title={widgetTitle(w)} widgetId={w.id}
                  dashId={dashboard?.id} dateFrom={dateFrom} dateTo={dateTo} showMode={showMode} pocanSube={pocanSube} aracTipi={aracTipi} aracTuru={aracTuru} currencyMode={currencyMode}
                  onCrossFilter={(column, value) => handleCrossFilter(w.id, column, value)} />
              </div>
            </div>
          </>
        )
      })()}
    </div>
  )
}


function FilterSection({ title, values, currentVal, search, onSearch, onSelect, onClear }) {
  const [expanded, setExpanded] = useState(false)

  // currentVal: array = secili olanlar, null/undefined = hepsi secili
  const selectedSet = new Set(
    Array.isArray(currentVal) ? currentVal : currentVal ? [currentVal] : []
  )
  // null/undefined → hepsi seçili. Boş array → hiçbiri seçili (Hepsini Kaldır).
  const allSelected = currentVal == null
  const noneSelected = Array.isArray(currentVal) && currentVal.length === 0

  const toggleValue = (val) => {
    if (allSelected) {
      // Hepsi seciliydi, sadece bu tiklanani sec (digerlerini kaldir)
      onSelect([val])
    } else if (selectedSet.has(val)) {
      // Tikini kaldir
      const next = [...selectedSet].filter(v => v !== val)
      if (next.length === 0 || next.length === values.length) onClear()
      else onSelect(next)
    } else {
      // Tik koy
      const next = [...selectedSet, val]
      if (next.length === values.length) onClear() // hepsi secildiyse filtre kaldir
      else onSelect(next)
    }
  }

  return (
    <div>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 w-full text-left mb-1">
        {expanded ? <FiChevronDown size={11} className="text-gray-400" /> : <FiChevronRight size={11} className="text-gray-400" />}
        <span className="text-[11px] font-medium text-gray-600 flex-1">{title}</span>
        {!allSelected && <span className="text-[9px] text-primary-600 bg-primary-50 px-1 rounded">{selectedSet.size}/{values.length}</span>}
      </button>

      {expanded && (
        <div className="ml-3">
          {values.length > 5 && (
            <div className="relative mb-1">
              <FiSearch size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-300" />
              <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Ara..."
                className="w-full pl-5 pr-2 py-0.5 border rounded text-[10px] bg-gray-50" />
            </div>
          )}

          <div className="flex gap-1 mb-1">
            <button onClick={onClear} className="text-[9px] text-primary-600 hover:underline">Tumunu Sec</button>
            <button onClick={() => onSelect([])} className="text-[9px] text-red-500 hover:underline">Hepsini Kaldir</button>
          </div>

          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {values.slice(0, 50).map(val => {
              const checked = allSelected || selectedSet.has(val)
              return (
                <label key={val} className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] cursor-pointer hover:bg-blue-50 ${
                  checked ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleValue(val)}
                    className="w-3 h-3 rounded border-gray-300 text-primary-600" />
                  <span className={`truncate ${!checked ? 'line-through' : ''}`}>{val}</span>
                </label>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
